import { useState } from 'react'
import {
  SyslogSettingsFormState,
  defaultSyslogSettingsFormState,
  syslogIsDifferent
} from '../syslog'
import {
  ServerSettingsFormSetterFactory,
  Server,
  ServerSettingsFormValue
} from '.'
import { isFeatureEnabledForLanguage, AgentFeature } from '../agent-features'
import { AgentLanguage } from '../agent-language'
import { SyslogAttackStatus } from '../syslog/syslog-attack-status'
import { SAMPLING_OPTIONS } from './server-settings-form-single-view'

// exported for testing
export function getSingleServerInitialState({
  name,
  environment,
  logPath,
  logLevel,
  botProtection,
  stacktraceCaptureMode,
  samplingProfile,
  sampling,
  syslogConfig
}: Server): ServerSettingsFormValue {
  const samplingEnabled = sampling && sampling.enabled
  const syslogEnabled = syslogConfig && syslogConfig.enabled
  return {
    name,
    environment,
    logPath,
    logLevel,
    botProtection,
    stacktraceCaptureMode,
    samplingProfile: samplingProfile || SAMPLING_OPTIONS.PROD, // default profile if not set
    samplingEnabled,
    samplingBaseline: (samplingEnabled && `${sampling.baseline}`) || '5',
    samplingFrequency: (samplingEnabled && `${sampling.frequency}`) || '10',
    samplingWindow: (samplingEnabled && `${sampling.window}`) || '183',
    syslogEnabled,
    syslog: {
      protocol:
        (syslogEnabled && syslogConfig.protocol) ||
        defaultSyslogSettingsFormState.protocol,
      host:
        (syslogEnabled && syslogConfig.ipAddress) ||
        defaultSyslogSettingsFormState.host,
      port:
        (syslogEnabled && syslogConfig.port) ||
        defaultSyslogSettingsFormState.port,
      facilityCode:
        (syslogEnabled && syslogConfig.facilityCode) ||
        defaultSyslogSettingsFormState.facilityCode,
      severityByAttackStatus: {
        [SyslogAttackStatus.EXPLOITED]:
          (syslogEnabled && syslogConfig.severityExploited) ||
          defaultSyslogSettingsFormState.severityByAttackStatus.EXPLOITED,
        [SyslogAttackStatus.SUSPICIOUS]:
          (syslogEnabled && syslogConfig.severitySuspicious) ||
          defaultSyslogSettingsFormState.severityByAttackStatus.SUSPICIOUS,
        [SyslogAttackStatus.BLOCKED]:
          (syslogEnabled && syslogConfig.severityBlocked) ||
          defaultSyslogSettingsFormState.severityByAttackStatus.BLOCKED,
        [SyslogAttackStatus.BLOCKED_PERIMETER]:
          (syslogEnabled && syslogConfig.severityBlockedPerimeter) ||
          defaultSyslogSettingsFormState.severityByAttackStatus
            .BLOCKED_PERIMETER,
        [SyslogAttackStatus.PROBED]:
          (syslogEnabled && syslogConfig.severityProbed) ||
          defaultSyslogSettingsFormState.severityByAttackStatus.PROBED
      }
    }
  }
}

export interface StateManagerProps {
  readonly server: Server
  readonly userIsProtect: boolean
  readonly agentFeatures: AgentFeature[]
  readonly isFreemium: boolean
  readonly children: (props: StateManagerChildrenProps) => any
}

export interface StateManagerChildrenProps {
  readonly formValue: ServerSettingsFormValue
  readonly isAllowedAssess: boolean
  readonly isAllowedProtect: boolean
  readonly isAllowedStackTraces: boolean
  readonly isAllowedSyslog: boolean
  readonly showLogPath: boolean
  readonly disableBotProtectToggle: boolean
  readonly disableSamplingToggle: boolean
  readonly disableSyslogToggle: boolean
  readonly showSampling: boolean
  readonly showSyslog: boolean

  readonly reset: () => void
  readonly updateSyslog: (syslog: SyslogSettingsFormState) => void
  readonly formSetter: ServerSettingsFormSetterFactory

  readonly hasUnsavedChanges: boolean
}

export const hasUnsavedChanges = (
  original: ServerSettingsFormValue,
  current: ServerSettingsFormValue
) => {
  if (
    original.name !== current.name ||
    original.environment !== current.environment ||
    original.logPath !== current.logPath ||
    original.logLevel !== current.logLevel ||
    original.botProtection !== current.botProtection ||
    original.stacktraceCaptureMode !== current.stacktraceCaptureMode ||
    original.samplingProfile !== current.samplingProfile ||
    original.samplingEnabled !== current.samplingEnabled ||
    original.samplingBaseline !== current.samplingBaseline ||
    original.samplingFrequency !== current.samplingFrequency ||
    original.samplingWindow !== current.samplingWindow ||
    original.syslogEnabled !== current.syslogEnabled ||
    syslogIsDifferent(original.syslog, current.syslog)
  ) {
    return true
  }
  return false
}

// Manages state and encapsulates business logic for the "singler server" form
// exported for testing
export function ServerSettingsFormSingleStateManager({
  server,
  agentFeatures,
  isFreemium,
  userIsProtect,
  children
}: StateManagerProps) {
  const [originalFormValue] = useState<ServerSettingsFormValue>(
    getSingleServerInitialState(server)
  )
  const [formValue, setState] = useState<ServerSettingsFormValue>(
    getSingleServerInitialState(server)
  )

  const formSetter: ServerSettingsFormSetterFactory =
    (key: keyof ServerSettingsFormValue) => (value) =>
      setState({
        ...formValue,
        [key]: value
      })

  const reset = () => setState(getSingleServerInitialState(server))

  const language = AgentLanguage.fromValue(server.language)

  const isAllowedProtect =
    userIsProtect &&
    isFeatureEnabledForLanguage({
      agentFeatures,
      featureName: 'protection',
      language
    })

  const isAllowedStackTraces = language.isDotNetFlavor()
  const disableSamplingToggle = !server.assess
  const disableSyslogToggle = !server.protect

  return children({
    formValue,
    isAllowedAssess: isFeatureEnabledForLanguage({
      agentFeatures,
      featureName: 'assess',
      language
    }),
    isAllowedProtect,
    isAllowedSyslog:
      isAllowedProtect &&
      !isFreemium &&
      isFeatureEnabledForLanguage({
        agentFeatures,
        featureName: 'syslog',
        language
      }),
    isAllowedStackTraces,
    showLogPath: !language.isDotNetFlavor(),
    disableBotProtectToggle: !server.protect,
    disableSamplingToggle,
    disableSyslogToggle,
    showSampling: !disableSamplingToggle && formValue.samplingEnabled,
    showSyslog: !disableSyslogToggle && formValue.syslogEnabled,
    reset,
    formSetter,
    updateSyslog: (syslog) => setState({ ...formValue, syslog }),
    hasUnsavedChanges: hasUnsavedChanges(originalFormValue, formValue)
  })
}
